/**
 * Customer 360 › WhatsApp › the numbers the team has linked to this customer (the owner, 2026-10-25: a link made in the WhatsApp chat did not show on the customer's page).
 * A linked number is a household member's phone (the mother who writes for the family): its WhatsApp messages are this customer's, and Nandini greets that person by the name saved here.
 * Only the last four digits are shown; unlinking needs the right to manage cases.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rpc } from '../../lib/supabase';
import { useSession } from '../../lib/session';
import { Button, EmptyState, ErrorState, LoadingRows, Panel, PanelHeader, plainError } from '../../ui/primitives';
import { toast } from '../../ui/toast';

interface Option { ref: string; label: string; kind: string; phone: string | null }
interface Options { options: Option[]; reminders: string | null; updates: string | null; remindersSet: boolean; updatesSet: boolean }
interface Link { ref: string; phone: string | null; personName: string; relation: string | null; linkedAt: string; by: string | null }

/**
 * Which of the customer's numbers gets what (the owner, 2026-10-25): the bills and reminders can go to one number (the husband's) while questions and order changes
 * come from another (hers); the updates can go to a third. Questions and order changes are answered on whichever number wrote, so that needs no setting.
 */
function ContactRoles({ customerId }: { customerId: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['wa-contact-options', customerId], queryFn: () => rpc<Options>('wa_contact_options', { p_customer_id: customerId }) });
  const [rem, setRem] = useState(''); const [upd, setUpd] = useState('');
  useEffect(() => { if (q.data) { setRem(q.data.remindersSet ? q.data.reminders ?? '' : ''); setUpd(q.data.updatesSet ? q.data.updates ?? '' : ''); } }, [q.data]);
  const save = useMutation({
    mutationFn: (v: { rem: string; upd: string }) => rpc<{ detail: string }>('wa_set_contact_roles', { p_customer_id: customerId, p_reminders_ref: v.rem || null, p_updates_ref: v.upd || null }),
    onSuccess: (r) => { toast(r.detail, { tone: 'ok' }); void qc.invalidateQueries({ queryKey: ['wa-contact-options', customerId] }); },
  });
  const edit = can('customer.edit') || can('service.case.manage');
  const select = 'rounded-[var(--radius-control)] border border-line bg-surface px-2 py-1 text-small';
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <LoadingRows rows={2} columns={3} />;
  const o = q.data;
  const dirty = rem !== (o.remindersSet ? o.reminders ?? '' : '') || upd !== (o.updatesSet ? o.updates ?? '' : '');
  const pick = (value: string, set: (v: string) => void, label: string) => (
    <label className="grid gap-0.5 text-small">{label}
      <select className={select} value={value} onChange={(e) => set(e.target.value)} disabled={!edit} aria-label={label}>
        <option value="">First number (as now)</option>
        {o.options.filter((x) => x.kind !== 'FIRST').map((x) => <option key={x.ref} value={x.ref}>{x.label} {x.phone ?? ''}</option>)}
      </select></label>);
  return (
    <div className="grid gap-2 border-b border-line pb-3">
      <div className="text-small font-medium">Which number gets what</div>
      <div className="flex flex-wrap items-end gap-3">
        {pick(rem, setRem, 'Bills and reminders go to')}
        {pick(upd, setUpd, 'Updates go to')}
        {edit ? <Button size="dense" variant="primary" disabled={save.isPending || !dirty} onClick={() => save.mutate({ rem, upd })}>Save</Button> : null}
      </div>
      <p className="text-small text-ink-muted">
        Bills, payment reminders, credit-limit reminders and the thank-you for a payment go to the number chosen for bills and reminders. Questions and order changes are answered on whichever of the customer&rsquo;s numbers writes (the first, the second, or one linked below), so nothing is set for those.
        The updates number is kept for delivery notices; none is sent yet.
      </p>
      {o.options.length < 2 ? <p className="text-small text-ink-muted">This customer has only one number. Add a second number in Edit customer, or link a family number from Automation › WhatsApp, and it will appear here.</p> : null}
      {save.error ? <div role="alert" className="text-small text-refused">{plainError((save.error as Error).message)}</div> : null}
    </div>
  );
}

interface Switches { billReminders: boolean; whatsappUpdates: boolean; stopped: boolean; changedBy: string | null; changedAt: string | null }

/**
 * The team's switches for what the system starts on WhatsApp (the owner, 2026-10-27: if it is off here it must not go; the customer only chooses which number gets it).
 * Bills and reminders off: no bill, due or overdue reminder, credit-limit reminder or thank-you is sent. A reply to the customer's own message and a reminder the team sends by hand are not affected.
 */
function MessageSwitches({ customerId }: { customerId: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['wa-message-switches', customerId], queryFn: () => rpc<Switches>('wa_message_switches', { p_customer_id: customerId }) });
  const save = useMutation({
    mutationFn: (v: { bill: boolean; upd: boolean }) => rpc<{ ok: boolean }>('wa_set_message_switches', { p_customer_id: customerId, p_bill_reminders: v.bill, p_whatsapp_updates: v.upd }),
    onSuccess: () => { toast('Saved.', { tone: 'ok' }); void qc.invalidateQueries({ queryKey: ['wa-message-switches', customerId] }); },
  });
  const edit = can('customer.edit') || can('service.case.manage');
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <LoadingRows rows={2} columns={3} />;
  const s = q.data;
  const box = (label: string, checked: boolean, onChange: (v: boolean) => void) => (
    <label className="flex items-center gap-2 text-small"><input type="checkbox" checked={checked} disabled={!edit || save.isPending} onChange={(e) => onChange(e.target.checked)} aria-label={label} />{label}</label>);
  return (
    <div className="grid gap-2 border-b border-line pb-3">
      <div className="text-small font-medium">What the system sends on WhatsApp</div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
        {box('Bills and reminders', s.billReminders, (v) => save.mutate({ bill: v, upd: s.whatsappUpdates }))}
        {box('Updates', s.whatsappUpdates, (v) => save.mutate({ bill: s.billReminders, upd: v }))}
      </div>
      <p className="text-small text-ink-muted">
        Switched off here, the bill, the due and overdue reminders, the credit-limit reminders and the thank-you for a payment are not sent to this customer. Replies to her own messages and a reminder you send by hand still go.
        {s.changedAt ? ` Last changed ${new Date(s.changedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })}${s.changedBy ? ` by ${s.changedBy === 'STAFF' ? 'the team' : s.changedBy}` : ''}.` : ''}
      </p>
      {s.stopped ? <p className="text-small text-ink-muted">The customer wrote STOP on WhatsApp.</p> : null}
      {save.error ? <div role="alert" className="text-small text-refused">{plainError((save.error as Error).message)}</div> : null}
    </div>
  );
}

export function LinkedNumbers({ customerId }: { customerId: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const links = useQuery({ queryKey: ['wa-customer-links', customerId], queryFn: () => rpc<Link[]>('wa_customer_links', { p_customer_id: customerId }) });
  const unlink = useMutation({
    mutationFn: (ref: string) => rpc<{ detail: string }>('wa_unlink_ref', { p_customer_id: customerId, p_ref: ref }),
    onSuccess: (r) => { toast(r.detail, { tone: 'ok' }); void qc.invalidateQueries({ queryKey: ['wa-customer-links', customerId] }); void qc.invalidateQueries({ queryKey: ['wa-conversations'] }); },
  });
  return (
    <Panel className="mt-3 overflow-hidden">
      <PanelHeader title="WhatsApp numbers of this customer" note="Which number gets the reminders and which the updates, and the household phones linked to this customer. Link one from Automation › WhatsApp." />
      <div className="grid gap-3 p-4">
        <ContactRoles customerId={customerId} />
        <MessageSwitches customerId={customerId} />
        {links.error ? <ErrorState error={links.error} retry={() => void links.refetch()} />
          : !links.data ? <LoadingRows rows={2} columns={3} />
          : links.data.length === 0 ? <EmptyState title="No number is linked" detail="When a family member writes from another phone, open the chat in Automation › WhatsApp and choose “Link this number to a customer”." />
          : (
            <ul className="grid gap-1">
              {links.data.map((l) => (
                <li key={l.ref} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                  <b>{l.personName}</b>{l.relation ? <span className="text-ink-muted">{l.relation}</span> : null}<span className="text-ink-muted">{l.phone ?? ''}</span>
                  <span className="text-small text-ink-muted">linked {new Date(l.linkedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })}{l.by ? ` by ${l.by}` : ''}</span>
                  {can('service.case.manage') ? <Button size="dense" variant="ghost" disabled={unlink.isPending} onClick={() => unlink.mutate(l.ref)}>Unlink</Button> : null}
                </li>))}
            </ul>)}
        {unlink.error ? <div role="alert" className="text-small text-refused">{plainError((unlink.error as Error).message)}</div> : null}
      </div>
    </Panel>
  );
}
